import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import PropTypes from "prop-types";
import KpiNamesModel from "models/KpiNamesModel";
import { addKpiName, deleteKpiName } from "services/api"; // Import delete API

import { motion } from "framer-motion";
import { useMaterialUIController } from "context";
import { useAuth } from "context/authContext";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import ConfirmationDialog from "components/ConfirmationDialog";

const AddKpiDialog = ({ open, onClose, perspectiveId, addKpi, kpiToEdit, removeKpi, allKpis }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState(false);
  const [nameErrorText, setNameErrorText] = useState("");
  const [formula, setFormula] = useState("+");
  const [description, setDescription] = useState("");
  const [theme, setTheme] = useState("");
  const [units, setUnits] = useState("");
  const [generatedFrom, setGeneratedFrom] = useState("");
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [confOpen, setConfOpen] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { companies } = useAuth();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    if (kpiToEdit) {
      setName(kpiToEdit.kpiName || "");
      setFormula(kpiToEdit.kpiFormula === "Regular" ? "+" : "-");
      setDescription(kpiToEdit.description || "");
      setTheme(kpiToEdit.theme || "");
      setUnits(kpiToEdit.units || "");
      setGeneratedFrom(kpiToEdit.generatedFrom || "");
      setSource(kpiToEdit.source || "");
    } else {
      setName("");
      setFormula("+");
      setDescription("");
      setTheme("");
      setUnits("");
      setGeneratedFrom("");
      setSource("");
    }
    setNameError(false); // Reset validation error when dialog opens
    setNameErrorText("");
  }, [kpiToEdit]);

  const handleSubmit = async () => {
    setLoading(true);
    if (!name.trim()) {
      setNameError(true);
      setNameErrorText(t("admin.inventory.dialog.KPINameIsRequired"));
      setLoading(false);
      return;
    }

    if (!kpiToEdit) {
      const isDuplicate = allKpis.some((kpi) => kpi.kpiName === name);

      if (isDuplicate) {
        setNameError(true);
        setNameErrorText(t("admin.inventory.dialog.KpiAlreadyExists"));
        setLoading(false);
        return;
      }
    }

    const newKpi = new KpiNamesModel({
      kpiName: name,
      perspectiveId: perspectiveId,
      kpiFormula: formula === "+" ? "Regular" : "Reverse",
      description: description,
      theme: theme,
      units: units,
      generatedFrom: generatedFrom,
      source: source,
      kpiNameId: kpiToEdit?.kpiNameId,
      companyId: companies ? companies[0]?.companyId : null,
    });

    const data = await addKpiName(newKpi);
    if (data !== "error") {
      const kpi = new KpiNamesModel(data) || newKpi;
      addKpi(kpi);
    } else {
      return "error";
    }
    setLoading(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!kpiToEdit) return;
    removeKpi(kpiToEdit.kpiNameId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? "#333" : "#fff", // Changes only the dialog background
          color: darkMode ? "#fff" : "#000", // Ensures text color is visible
          borderRadius: 2, // Optional: Smooth rounded corners
          padding: 2, // Optional: Adds some spacing inside
        },
      }}
      fullWidth
    >
      <DialogTitle>
        {kpiToEdit ? t("admin.inventory.dialog.EditKPI") : t("admin.inventory.dialog.AddNewKPI")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.dialog.Name") + " *"}
              fullWidth
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) {
                  setNameError(false);
                  setNameErrorText("");
                }
              }}
              error={nameError}
              helperText={nameErrorText}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label={t("admin.inventory.dialog.Formula")}
              fullWidth
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              sx={fieldStyles}
            >
              <MenuItem value="+">+</MenuItem>
              <MenuItem value="-">-</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.dialog.Description")}
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.dialog.Theme")}
              fullWidth
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.dialog.Units")}
              fullWidth
              value={units}
              onChange={(e) => setUnits(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.dialog.GeneratedFrom")}
              fullWidth
              value={generatedFrom}
              onChange={(e) => setGeneratedFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t("admin.inventory.dialog.Source")}
              fullWidth
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      {!loading ? (
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            {t("admin.inventory.dialog.Cancel")}
          </Button>
          {kpiToEdit && (
            <Button
              onClick={() => setConfOpen(true)}
              color="error"
              sx={{
                color: "red",
                "&:hover": { backgroundColor: "red", color: "#fff" },
              }}
            >
              {t("admin.inventory.dialog.Delete")}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            color="primary"
            sx={{
              "&:hover": { backgroundColor: "blue", color: "#fff" },
            }}
          >
            {kpiToEdit ? t("admin.inventory.dialog.Update") : t("admin.inventory.dialog.Add")}
          </Button>
        </DialogActions>
      ) : (
        <Box display="flex" justifyContent="flex-end" p={1}>
          <CircularProgress size={20} sx={{ color: "black" }} />
        </Box>
      )}
      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        message={
          t("admin.inventory.dialog.confirm_delete_kpi") ||
          "Are you sure you want to delete this KPI?"
        }
        onConfirm={async () => {
          setLoadingDelete(true);
          try {
            await handleDelete();
            setConfOpen(false);
          } catch (err) {
            snackbarRef.current?.showSnackbar("Error deleting KPI.", "error");
          } finally {
            setLoadingDelete(false);
          }
        }}
      />
    </Dialog>
  );
};

AddKpiDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  perspectiveId: PropTypes.number.isRequired,
  addKpi: PropTypes.func.isRequired,
  removeKpi: PropTypes.func.isRequired,
  allKpis: PropTypes.array.isRequired,
  kpiToEdit: PropTypes.shape({
    kpiNameId: PropTypes.number,
    kpiName: PropTypes.string,
    perspectiveId: PropTypes.number,
    kpiFormula: PropTypes.string,
    description: PropTypes.string,
    theme: PropTypes.string,
    accountability: PropTypes.string,
    units: PropTypes.string,
    generatedFrom: PropTypes.string,
    source: PropTypes.string,
  }),
};

export default AddKpiDialog;
